import _ from 'lodash'
import { ref } from 'vue'

import { useSessionStore } from '@/stores/session'

export const useFormValidation = (options = {}) => {
  const sessionStore = useSessionStore()

  const isValidationRun = ref(false)
  const isLoaderVisible = ref(false)

  const pushDataToForm = (data, form) => {
    if (data) {
      Object.values(form).forEach((field) => {
        if (Object.keys(data).includes(field.title)) {
          field.value = data[field.title]
        }
      })
    }
  }

  const pushFormToData = (data, form) => {
    Object.values(form).forEach((field) => {
      if (field.title) {
        data[field.title] = field.value
      }
    })
  }

  const mapFormErrors =
    options.mapFormErrors ||
    ((errors, form) => {
      if (errors && errors.length) {
        form._errors = []
        errors.forEach((error) => {
          const field = _.find(form, ['title', error.subject])
          if (field) {
            delete field.errors.server
            field.errors = { ...field.errors, server: error.message }
          } else {
            if (!error.message) error.message = 'Something went wrong'
            form._errors.push(error)
          }
        })
      }
    })

  const mapFormValidationErrors =
    options.mapFormValidationErrors ||
    ((errors, form) => {
      if (Array.isArray(errors)) {
        const filteredErrors = errors.filter((error) =>
          ['ValidationError', 'invalid_grant', 'unauthorized'].includes(error.type),
        )
        mapFormErrors(filteredErrors, form)
      }
    })

  const onFieldChange = (field, v$) => {
    if (v$) v$.$touch()
    if (field && field.errors) {
      clearFieldServerError(field)
    }
  }

  const onFieldChangeGuestCheckout = (fieldName, field, v$) => {
    const formField = v$.form[fieldName]
    if (formField) formField.$touch()
    if (field && field.errors) {
      clearFieldServerError(field)
    }
  }

  const clearFieldServerError = (field) => {
    delete field.errors.server
  }

  const clearFormServerErrors = (form) => {
    form._errors = []
  }

  const withPresendRoutine = (cb) => {
    if (options.isModelValid.value) {
      return cb()
    } else {
      isValidationRun.value = true
    }
  }

  const updatePhoneMask = (phone, countryIsoCode) => {
    if (!countryIsoCode || !phone?.mask) return
    phone.mask = ['US', 'CA'].includes(countryIsoCode) ? '(###) ### - ####' : '+(##) ##-#### ####'
  }

  const sendForm = ({ action, data, asyncCallback, form }) => {
    isLoaderVisible.value = true
    isValidationRun.value = true
    return new Promise((resolve, reject) => {
      action(data)
        .then(async (res) => {
          if (asyncCallback) await asyncCallback()
          isValidationRun.value = false
          resolve(res)
        })
        .catch((reason) => {
          if (reason.response && reason.response.data && reason.response.data.length) {
            mapFormValidationErrors(reason.response?.data, form)
          }
          reject(reason)
        })
        .finally(() => {
          isLoaderVisible.value = false
        })
    })
  }

  const updateExtension = (extensionField, countryIsoCode) => {
    if (!extensionField || !countryIsoCode) return
    const maxLength = ['US', 'CA'].includes(countryIsoCode) ? 10 : 8
    extensionField.maxLength = maxLength
    extensionField.errors.maxLength = `Max length of ${maxLength} characters.`
  }

  const updateStateField = ({ countryField, stateField, phoneField, extensionField }) => {
    const countryIsoCode = countryField.value.isocode
    if (phoneField) updatePhoneMask(phoneField, countryIsoCode)
    if (extensionField) updateExtension(extensionField, countryIsoCode)

    if (['US', 'CA'].includes(countryIsoCode)) {
      stateField.options = sessionStore.regions[countryIsoCode]
      stateField.placeholder = 'State/Province/Region *'
      if (
        !sessionStore.regions[countryIsoCode]?.some((el) => el.isocode === stateField.value.isocode)
      )
        stateField.value = {
          name: '',
        }
    } else {
      stateField.options = null
      stateField.placeholder = 'State/Province/Region'
      stateField.value = {
        name: '',
      }
    }
  }

  return {
    pushDataToForm,
    pushFormToData,
    mapFormErrors,
    mapFormValidationErrors,
    onFieldChange,
    onFieldChangeGuestCheckout,
    clearFieldServerError,
    clearFormServerErrors,
    withPresendRoutine,
    updatePhoneMask,
    sendForm,
    isValidationRun,
    isLoaderVisible,
    updateExtension,
    updateStateField,
  }
}
